// The atlas cells of the fairway set of mockup round 115 (R2723,
// ui-map-interface.md §17.3.50, §17.3.70): the soft glow of card 02 Glow
// Channel, the four buoys of card 02 cut to their shapes, and the four
// engraved buoys of card 07 Engraved Survey.
//
// A buoy is a filled polygon, which no instance batch draws, so each shape
// is ONE atlas cell, painted white once and tinted by the quad (§10). The
// glow is the page's `createRadialGradient`, which a batch cannot make
// either. The sizes come from `[chart.piece.fairways]` of
// `data/presentation/chart.toml`; the outline of each shape is the card's
// drawing, in units of that size.
//
// This is a leaf: it reads the generated tables and paints into the context
// the atlas hands it.

import { CHART } from '../gen/presentation';
import type { AtlasSpec } from '../fast/gfx/atlas';

/** The kind of a buoy, as the wire `BuoyKind` names it from 1. */
export type BuoyShape = 'port' | 'starboard' | 'mark' | 'junction';
export const BUOY_SHAPES: readonly BuoyShape[] = ['port', 'starboard', 'mark', 'junction'];

const T = CHART.pieces.fairways;

/** The glow cell: its radius in world px at sprite scale 1. A caller
 *  scales the sprite by the radius it wants over this. */
export const GLOW_CELL_R = 32;

/** The key of the glow cell. */
export const GLOW_KEY = 'fw:glow';

/** The key of a buoy cell in a reading: `fw:m:<kind>` or `fw:h:<kind>`. */
export function buoyCellKey(historical: boolean, kind: BuoyShape): string {
  return `fw:${historical ? 'h' : 'm'}:${kind}`;
}

/** Supersampling of the buoy cells: a buoy is a few px across. */
const BUOY_SCALE = 4;

/** The glow ramp: the stops of `glow_stops` at the alphas of
 *  `glow_stop_alpha`, white, so the quad tints it. */
function glowSpec(key: string): AtlasSpec {
  const r = GLOW_CELL_R;
  return {
    key,
    scale: 1,
    box: { x: -r, y: -r, w: 2 * r, h: 2 * r },
    draw(ctx) {
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      const at = T.arr.glow_stops, a = T.arr.glow_stop_alpha;
      for (let i = 0; i < at.length; i++) g.addColorStop(at[i], `rgba(255,255,255,${a[i] ?? 0})`);
      ctx.fillStyle = g;
      ctx.fillRect(-r, -r, 2 * r, 2 * r);
    },
  };
}

/** Card 02: a bright pip cut to its shape, of radius `r`. */
function modernBuoy(ctx: CanvasRenderingContext2D, kind: BuoyShape, r: number): void {
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  if (kind === 'port') ctx.rect(-r * 0.8, -r * 0.8, r * 1.6, r * 1.6);
  else if (kind === 'starboard') { ctx.moveTo(0, -r); ctx.lineTo(r * 0.95, r * 0.75); ctx.lineTo(-r * 0.95, r * 0.75); ctx.closePath(); }
  else if (kind === 'mark') ctx.arc(0, 0, r * 0.8, 0, Math.PI * 2);
  else {
    // Two thin diamonds across each other: where two chains meet.
    const l = r * 2.1, w = r * 0.42;
    ctx.moveTo(0, -l); ctx.lineTo(w, 0); ctx.lineTo(0, l); ctx.lineTo(-w, 0); ctx.closePath();
    ctx.moveTo(-l, 0); ctx.lineTo(0, w); ctx.lineTo(l, 0); ctx.lineTo(0, -w); ctx.closePath();
  }
  ctx.fill();
}

/** Card 07: the engraved side view standing on its waterline at y = 0, in
 *  units of `u` px: a can, a nun, a spar with a ball and a bell. */
function historicalBuoy(ctx: CanvasRenderingContext2D, kind: BuoyShape, u: number): void {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const poly = (pts: readonly (readonly [number, number])[]): void => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x * u, y * u) : ctx.moveTo(x * u, y * u)));
    ctx.closePath();
  };
  const disc = (x: number, y: number, r: number): void => {
    ctx.beginPath(); ctx.arc(x * u, y * u, r * u, 0, Math.PI * 2); ctx.fill();
  };
  if (kind === 'port') { poly([[-2.6, 0], [-2.2, -6], [2.2, -6], [2.6, 0]]); ctx.fill(); return; }
  if (kind === 'starboard') { poly([[-3, 0], [0, -8], [3, 0]]); ctx.fill(); return; }
  if (kind === 'mark') {
    ctx.lineWidth = u;
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -9 * u); ctx.stroke();
    disc(0, -10, 1.8);
    return;
  }
  poly([[-3, 0], [-1.8, -7], [1.8, -7], [3, 0]]);
  ctx.lineWidth = u; ctx.stroke();
  ctx.lineWidth = 0.8 * u;
  ctx.beginPath(); ctx.moveTo(-2.4 * u, -3.5 * u); ctx.lineTo(2.4 * u, -3.5 * u); ctx.stroke();
  disc(0, -8.5, 1.3);
}

/** The spec of a `fw:` key, or null for any other key. */
export function fairwaySpecFor(key: string): AtlasSpec | null {
  if (!key.startsWith('fw:')) return null;
  if (key === GLOW_KEY) return glowSpec(key);
  const p = key.split(':');
  if (p.length !== 3 || (p[1] !== 'm' && p[1] !== 'h')) return null;
  const kind = p[2] as BuoyShape;
  if (!BUOY_SHAPES.includes(kind)) return null;
  if (p[1] === 'm') {
    const r = T.modern.fig.buoy_px;
    const h = r * 2.5;
    return { key, scale: BUOY_SCALE, box: { x: -h, y: -h, w: 2 * h, h: 2 * h }, draw: (ctx) => modernBuoy(ctx, kind, r) };
  }
  const u = T.historical.fig.buoy_unit_px;
  return {
    key,
    scale: BUOY_SCALE,
    box: { x: -4 * u, y: -12.5 * u, w: 8 * u, h: 14 * u },
    draw: (ctx) => historicalBuoy(ctx, kind, u),
  };
}
